import { EntityList } from '@/components';
import { useApi } from '@/api';
import { DEFAULT_NETWORK } from '@/constants';
import {
  CryptoIcon,
  DataTable,
  Flex,
  Heading,
  PercentageValue,
  TableSkeleton
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

type Asset = {
  address: string;
  symbol: string;
};

type EmodeParamsData = {
  category: number;
  label: string;
  ltv: number;
  liquidation_threshold: number;
  liquidation_bonus: number;
  collateral: Asset[];
  borrowable: Asset[];
};

export default function EmodeParamsTable() {
  const network = DEFAULT_NETWORK;
  const [data, error] = useApi<EmodeParamsData[]>({
    url: '/params/emode/'
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={3} columns={6} />;
  }
  const columns: TableColumn<EmodeParamsData>[] = [
    {
      header: 'Category',
      getCell: (row) => row.category,
      columnSize: 'auto'
    },
    {
      header: 'Label',
      getCell: (row) => row.label
    },
    {
      header: 'Collateral',
      getCell: (row) =>
        row.collateral.length > 0 ? (
          <EntityList showTotalNumber={false}>
            {row.collateral.map((asset: Asset) => {
              return (
                <CryptoIcon
                  key={`col-${row.category}${asset.address}`}
                  name={asset.symbol}
                  address={asset.address}
                  size="3"
                  network={network}
                />
              );
            })}
          </EntityList>
        ) : null,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },

    {
      header: 'Borrowable',
      getCell: (row) =>
        row.borrowable.length > 0 ? (
          <EntityList showTotalNumber={false}>
            {row.borrowable.map((asset: Asset) => {
              return (
                <CryptoIcon
                  key={`bor-${row.category}${asset.address}`}
                  name={asset.symbol}
                  address={asset.address}
                  size="3"
                  network={network}
                />
              );
            })}
          </EntityList>
        ) : null,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'LTV',
      getCell: (row) => <PercentageValue value={row.ltv} />,
      sortingField: 'ltv',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Liquidation Threshold',
      getCell: (row) => <PercentageValue value={row.liquidation_threshold} />,
      sortingField: 'liquidation_threshold',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Liquidation Bonus',
      getCell: (row) => <PercentageValue value={row.liquidation_bonus} />,
      sortingField: 'liquidation_bonus',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <Flex flexDirection="column" gap="s">
      <Heading size="3">Emode Parameters</Heading>
      <DataTable rows={data} columns={columns} getKey={(row) => row.category} />
    </Flex>
  );
}
